import CheckIcon from '@mui/icons-material/Check';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { Path, PathValue, useFormContext } from 'react-hook-form';

import { CustomTableQuickFilter } from '@/components/table/quickFilters/CustomTableQuickFilter.model.ts';
import { resolveQuickFilterToggle } from '@/components/table/quickFilters/quickFilter.util.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props<MODEL extends object> = {
  appliedParams: MODEL;
  filters: CustomTableQuickFilter<MODEL>[];
  isLoading: boolean;
  onApply: () => Promise<void>;
};

export const CustomTableQuickFilters = <MODEL extends object>({
  appliedParams,
  filters,
  isLoading,
  onApply,
}: Props<MODEL>) => {
  const { t } = useTranslationWithPrefix('components.table.content');
  const { getValues, setValue } = useFormContext<MODEL>();

  const handleToggle = async (filter: CustomTableQuickFilter<MODEL>) => {
    const changes = resolveQuickFilterToggle(
      filter,
      filters,
      getValues(),
      filter.isActive(appliedParams),
    );
    Object.entries(changes).forEach(([key, value]) => {
      setValue(key as Path<MODEL>, value as PathValue<MODEL, Path<MODEL>>);
    });
    await onApply();
  };

  return (
    <Stack direction="row" sx={{ alignItems: 'center', columnGap: 1, flexWrap: 'wrap', rowGap: 1 }}>
      <Typography sx={{ color: themeColors.greyDark, mr: 0.5 }} variant="f12Regular">
        {t('quickFilters')}
      </Typography>
      {filters.map(filter => {
        const isActive = filter.isActive(appliedParams);
        const chip = (
          <Chip
            clickable
            color={isActive ? 'primary' : 'default'}
            disabled={isLoading}
            icon={isActive ? <CheckIcon /> : filter.icon}
            key={filter.id}
            label={filter.label}
            onClick={() => void handleToggle(filter)}
            size="small"
            sx={{
              '& .MuiChip-icon': {
                color: isActive ? themeColors.accentColorDark : themeColors.greyDark,
                fontSize: 16,
              },
              fontWeight: isActive ? 500 : 400,
            }}
            variant="outlined"
          />
        );
        if (!filter.tooltip) {
          return chip;
        }
        return (
          <Tooltip key={filter.id} title={filter.tooltip}>
            <span>{chip}</span>
          </Tooltip>
        );
      })}
    </Stack>
  );
};
